import { useEffect, useRef, useState } from 'react'
import { Chart, registerables } from 'chart.js'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import api from '@/lib/api'
import type { PriceHistory } from '@/types'

Chart.register(...registerables)

type TimeRange = '1W' | '1M' | '3M' | '6M' | '1Y' | '5Y'

const TIME_RANGES: { label: string; value: TimeRange; days: number }[] = [
  { label: '1W', value: '1W', days: 7 },
  { label: '1M', value: '1M', days: 30 },
  { label: '3M', value: '3M', days: 90 },
  { label: '6M', value: '6M', days: 183 },
  { label: '1Y', value: '1Y', days: 365 },
  { label: '5Y', value: '5Y', days: 1825 },
]

interface PriceChartProps {
  ticker: string
}

export function PriceChart({ ticker }: PriceChartProps) {
  const chartRef = useRef<HTMLCanvasElement>(null)
  const chartInstance = useRef<Chart | null>(null)
  const [selectedRange, setSelectedRange] = useState<TimeRange>('1M')
  const [priceData, setPriceData] = useState<PriceHistory | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch price data
  useEffect(() => {
    async function fetchPrices() {
      setIsLoading(true)
      setError(null)
      try {
        const days = TIME_RANGES.find((r) => r.value === selectedRange)?.days || 30
        const response = await api.get(`/investments/prices/${encodeURIComponent(ticker)}`, {
          params: { days },
        })
        setPriceData(response.data)
      } catch (err) {
        console.error('Failed to fetch price data:', err)
        setError('Price data unavailable')
      } finally {
        setIsLoading(false)
      }
    }
    fetchPrices()
  }, [ticker, selectedRange])

  // Render chart
  useEffect(() => {
    if (!chartRef.current || !priceData || priceData.prices.length === 0) return

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy()
    }

    const ctx = chartRef.current.getContext('2d')
    if (!ctx) return

    const prices = priceData.prices
    const labels = prices.map((p) => {
      const date = new Date(p.date)
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    })
    const data = prices.map((p) => p.price)

    // Calculate if overall trend is up or down
    const firstPrice = data[0] || 0
    const lastPrice = data[data.length - 1] || 0
    const isPositive = lastPrice >= firstPrice
    const lineColor = isPositive ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'
    const gradientColor = isPositive ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)'

    // Create gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, 160)
    gradient.addColorStop(0, gradientColor)
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)')

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            data,
            borderColor: lineColor,
            backgroundColor: gradient,
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: lineColor,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (context) => {
                const value = context.parsed.y
                return value != null ? `$${value.toFixed(2)}` : ''
              },
            },
          },
        },
        scales: {
          x: {
            display: false,
          },
          y: {
            display: false,
          },
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
      },
    })

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy()
      }
    }
  }, [priceData])

  // Calculate price change
  const priceChange = priceData && priceData.prices.length >= 2
    ? priceData.prices[priceData.prices.length - 1].price - priceData.prices[0].price
    : null
  const priceChangePercent = priceChange !== null && priceData && priceData.prices[0].price > 0
    ? (priceChange / priceData.prices[0].price) * 100
    : null
  const isPositive = (priceChange ?? 0) >= 0

  return (
    <div className="space-y-3">
      {/* Time range selector */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {TIME_RANGES.map((range) => (
            <button
              key={range.value}
              onClick={() => setSelectedRange(range.value)}
              className={cn(
                'px-2 py-1 text-xs font-medium rounded transition-colors',
                selectedRange === range.value
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
              )}
            >
              {range.label}
            </button>
          ))}
        </div>
        {priceChange !== null && (
          <div className={cn('text-sm font-medium', isPositive ? 'text-success' : 'text-danger')}>
            {isPositive ? '+' : ''}${priceChange.toFixed(2)}
            {priceChangePercent !== null && (
              <span className="ml-1">
                ({isPositive ? '+' : ''}{priceChangePercent.toFixed(1)}%)
              </span>
            )}
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="h-40 relative">
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : error ? (
          <div className="flex items-center justify-center h-full text-sm text-gray-500">
            {error}
          </div>
        ) : priceData && priceData.prices.length > 0 ? (
          <canvas ref={chartRef} />
        ) : (
          <div className="flex items-center justify-center h-full text-sm text-gray-500">
            No price data available
          </div>
        )}
      </div>
    </div>
  )
}
